import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Calendar,
  Loader2,
} from "lucide-react";
import { exportToCsv, exportMultiSheetExcel } from "@/lib/reportExport";
import { toast } from "sonner";
import { getTeamPrimaryPlayers } from "@/lib/teamHelpers";

interface Props {
  leagueId: string;
  leagueName: string;
}

// TASK-67 PR-D: row shape returned by the team_members embed. Each row
// is normalized to { user_id, customer_id, role, joined_at } — the
// helper getTeamPrimaryPlayers picks the first 2 primaries by joined_at
// (matches the server-side trigger semantics in PR-A's
// sync_league_match_to_match_results migration).
type TeamMemberLite = {
  user_id: string | null;
  customer_id: string | null;
  role: "primary" | "reserve";
  joined_at: string;
};

type TeamRowForExport = {
  id: string;
  team_name: string | null;
  payment_status: string | null;
  status: string | null;
  division_id: string | null;
  divisions: { name: string } | null;
  members: TeamMemberLite[] | null;
};

// TASK-67 PR-D: profiles table has no `email` column (verified via
// generated types — email lives on auth.users + the customers row).
// The legacy code SELECTed email anyway and silently failed (typed
// `as never`); the new path keeps email blank for the registered
// branch and falls back to the customers row when present.
type ProfileLite = {
  id: string;
  full_name: string | null;
  phone: string | null;
  level: number | null;
};

type CustomerLite = {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
};

export default function LeagueCSVExport({ leagueId, leagueName }: Props) {
  const { t } = useTranslation("admin");
  const [loading, setLoading] = useState(false);

  async function fetchTeamsData() {
    // TASK-66 PR-D: dropped captain_id + captain_customer_id from SELECT.
    // TASK-67 PR-D: dropped player1_id, player2_id, player1_customer_id,
    // player2_customer_id from SELECT — roster identity is now sourced
    // from the team_members embed and the canonical pair is the first
    // 2 primaries by joined_at (`getTeamPrimaryPlayers`). PR-E drops
    // those columns from the schema entirely.
    const { data: teams } = await supabase
      .from("teams")
      .select(
        "id, team_name, payment_status, status, division_id, divisions(name), members:team_members(user_id, customer_id, role, joined_at)"
      )
      .eq("league_id", leagueId);

    const typedTeams = (teams ?? []) as unknown as TeamRowForExport[];

    // Collect every primary's user_id + customer_id (across all teams)
    // so we can batch-load profile / customer details in one round-trip.
    const playerUserIds = [
      ...new Set(
        typedTeams
          .flatMap((t) =>
            getTeamPrimaryPlayers(t.members ?? [])
              .slice(0, 2)
              .map((m) => m.user_id),
          )
          .filter((id): id is string => id !== null),
      ),
    ];
    // INTENTIONAL: bucket-C admin-PII read via "Admin all profiles" RLS policy.
    // CSV export includes phone for org-admin contact list of league players.
    const { data: profiles } = playerUserIds.length > 0
      ? await supabase
          .from("profiles")
          .select("id, full_name, phone, level")
          .in("id", playerUserIds)
      : { data: [] as ProfileLite[] };
    const profileMap = new Map<string, ProfileLite>(
      (profiles ?? []).map((p) => [p.id, p as ProfileLite]),
    );

    const customerIds = [
      ...new Set(
        typedTeams
          .flatMap((t) =>
            getTeamPrimaryPlayers(t.members ?? [])
              .slice(0, 2)
              .map((m) => m.customer_id),
          )
          .filter((id): id is string => id !== null),
      ),
    ];
    const { data: customers } = customerIds.length > 0
      ? await supabase
          .from("customers")
          .select("id, full_name, email, phone")
          .in("id", customerIds)
      : { data: [] as CustomerLite[] };
    const customerMap = new Map<string, CustomerLite>(
      (customers ?? []).map((c) => [c.id, c as CustomerLite]),
    );

    const teamIds = typedTeams.map((t) => t.id);
    const { data: prefs } = await supabase
      .from("league_team_preferences" as any)
      .select("*")
      .in("team_id", teamIds);
    const prefMap = new Map(
      (prefs ?? []).map((p: any) => [p.team_id, p])
    );

    return { teams: typedTeams, profileMap, customerMap, prefMap };
  }

  // TASK-67 PR-D: resolve a team_members row (primary slot 0 or 1) to a
  // CSV-row's worth of fields. Mirrors the legacy resolveSlot helper but
  // takes a member shape (user_id OR customer_id) instead of separate
  // player slots. Returns empty placeholders when the slot is missing
  // (e.g. a solo-registration team only has 1 primary).
  function resolveSlot(
    member: TeamMemberLite | null,
    profileMap: Map<string, ProfileLite>,
    customerMap: Map<string, CustomerLite>,
  ) {
    if (!member) {
      return { fullName: "", email: "", phone: "", level: "" as string | number, registered: true };
    }
    if (member.user_id) {
      const profile = profileMap.get(member.user_id);
      return {
        fullName: profile?.full_name || "",
        // TASK-67 PR-D: profiles has no email column. Email/contact for
        // registered members is no longer surfaced in CSV exports here
        // (was effectively blank pre-PR-D too — the legacy SELECT silently
        // dropped the column). A future enhancement could join via
        // customers.email but that requires the org-scope filter.
        email: "",
        phone: profile?.phone || "",
        level: (profile?.level as number | string) ?? "",
        registered: true,
      };
    }
    if (member.customer_id) {
      const customer = customerMap.get(member.customer_id);
      return {
        fullName: customer?.full_name || "",
        email: customer?.email || "",
        phone: customer?.phone || "",
        level: "" as string | number,
        registered: false,
      };
    }
    return { fullName: "", email: "", phone: "", level: "" as string | number, registered: true };
  }

  function buildTeamRows(
    teams: TeamRowForExport[],
    profileMap: Map<string, ProfileLite>,
    customerMap: Map<string, CustomerLite>,
    prefMap: Map<string, any>
  ) {
    return teams.map((t) => {
      // TASK-67 PR-D: derive (slot1, slot2) from the first 2 primaries by
      // joined_at — same pair the trigger writes into match_results.
      const primaries = getTeamPrimaryPlayers(t.members ?? []);
      const s1 = resolveSlot(primaries[0] ?? null, profileMap, customerMap);
      const s2 = resolveSlot(primaries[1] ?? null, profileMap, customerMap);
      const pref = prefMap.get(t.id) as any;
      const [fn1, ...ln1] = s1.fullName.split(" ");
      const [fn2, ...ln2] = s2.fullName.split(" ");
      // UPPDRAG-62 PR-D: append "(Ej registrerad)" suffix to the name parts
      // for customer-only slots so admins see who's still pending signup.
      const suffix1 = s1.registered ? "" : " (Ej registrerad)";
      const suffix2 = s2.registered ? "" : " (Ej registrerad)";
      return {
        Lagnamn:
          t.team_name || `${ln1.join(" ")} / ${ln2.join(" ")}`,
        "Förnamn 1": fn1 + suffix1,
        "Efternamn 1": ln1.join(" "),
        "Mail 1": s1.email,
        "Telefon 1": s1.phone,
        "Nivå 1": s1.level,
        "Förnamn 2": fn2 + suffix2,
        "Efternamn 2": ln2.join(" "),
        "Mail 2": s2.email,
        "Telefon 2": s2.phone,
        "Nivå 2": s2.level,
        Division: (t.divisions as any)?.name || "",
        Betalstatus: t.payment_status || "",
        "Tillgängliga dagar":
          pref?.available_days?.join(", ") || "",
        Övrigt: pref?.notes || "",
      };
    });
  }

  function buildBillingRows(
    teams: TeamRowForExport[],
    profileMap: Map<string, ProfileLite>,
    customerMap: Map<string, CustomerLite>
  ) {
    return teams.map((t) => {
      const primaries = getTeamPrimaryPlayers(t.members ?? []);
      const s1 = resolveSlot(primaries[0] ?? null, profileMap, customerMap);
      const s2 = resolveSlot(primaries[1] ?? null, profileMap, customerMap);
      const [fn1, ...ln1] = s1.fullName.split(" ");
      const [fn2, ...ln2] = s2.fullName.split(" ");
      const suffix1 = s1.registered ? "" : " (Ej registrerad)";
      const suffix2 = s2.registered ? "" : " (Ej registrerad)";
      return {
        Lagnamn:
          t.team_name || `${ln1.join(" ")} / ${ln2.join(" ")}`,
        "Förnamn 1": fn1 + suffix1,
        "Efternamn 1": ln1.join(" "),
        "Mail 1": s1.email,
        "Förnamn 2": fn2 + suffix2,
        "Efternamn 2": ln2.join(" "),
        "Mail 2": s2.email,
        Betalstatus: t.payment_status || "",
      };
    });
  }

  async function fetchScheduleRows() {
    const { data: matches } = await supabase
      .from("league_matches")
      .select(
        "round, scheduled_date, scheduled_time, status, court_id, home_team_id, away_team_id, courts(name), divisions(name)"
      )
      .eq("league_id", leagueId)
      .order("round")
      .order("scheduled_date");

    const teamIds = [
      ...new Set(
        (matches ?? []).flatMap((m) => [
          m.home_team_id,
          m.away_team_id,
        ])
      ),
    ];
    // TASK-67 PR-D: same embed swap as fetchTeamsData — drop player1/2
    // columns, source roster identity from team_members.
    const { data: teams } = await supabase
      .from("teams")
      .select("id, team_name, members:team_members(user_id, customer_id, role, joined_at)")
      .in("id", teamIds);
    const typedTeams = (teams ?? []) as unknown as Array<
      Pick<TeamRowForExport, "id" | "team_name" | "members">
    >;
    const playerIds = [
      ...new Set(
        typedTeams
          .flatMap((t) =>
            getTeamPrimaryPlayers(t.members ?? [])
              .slice(0, 2)
              .map((m) => m.user_id),
          )
          .filter((id): id is string => id !== null),
      ),
    ];
    const { data: profiles } = playerIds.length > 0
      ? await supabase.from("public_profiles" as any).select("id, full_name").in("id", playerIds)
      : { data: [] as { id: string; full_name: string | null }[] };
    const profileMap = new Map(
      profiles?.map((p) => [p.id, p]) ?? []
    );
    const customerIds = [
      ...new Set(
        typedTeams
          .flatMap((t) =>
            getTeamPrimaryPlayers(t.members ?? [])
              .slice(0, 2)
              .map((m) => m.customer_id),
          )
          .filter((id): id is string => id !== null),
      ),
    ];
    const { data: customers } = customerIds.length > 0
      ? await supabase.from("customers").select("id, full_name, email").in("id", customerIds)
      : { data: [] as { id: string; full_name: string | null; email: string | null }[] };
    const customerMap = new Map(customers?.map((c) => [c.id, c]) ?? []);
    const teamNameMap = new Map(
      typedTeams.map((t) => {
        // TASK-67 PR-D: build the "Andersson / Eriksson" fallback from
        // the first 2 primaries' last names; falls back to "Ej reg" /
        // "?" for missing slots, matching pre-PR-D behavior.
        const primaries = getTeamPrimaryPlayers(t.members ?? []);
        const slotName = (m: TeamMemberLite | null): string => {
          if (!m) return "?";
          if (m.user_id) return profileMap.get(m.user_id)?.full_name?.split(" ").pop() || "?";
          if (m.customer_id) return customerMap.get(m.customer_id)?.full_name?.split(" ").pop() || "Ej reg";
          return "?";
        };
        const n1 = slotName(primaries[0] ?? null);
        const n2 = slotName(primaries[1] ?? null);
        const name = t.team_name || `${n1} / ${n2}`;
        return [t.id, name];
      })
    );

    return (matches ?? []).map((m) => ({
      Omgång: m.round,
      Datum: m.scheduled_date || "",
      Tid: m.scheduled_time || "",
      Bana: (m.courts as any)?.name || "",
      Hemmalag: teamNameMap.get(m.home_team_id) || "",
      Bortalag: teamNameMap.get(m.away_team_id) || "",
      Division: (m.divisions as any)?.name || "",
      Status: m.status || "",
    }));
  }

  async function withLoading(fn: () => Promise<void>) {
    setLoading(true);
    try {
      await fn();
      toast.success(t("leagues.export.done"));
    } catch (err) {
      console.error(err);
      toast.error(t("leagues.export.failed"));
    } finally {
      setLoading(false);
    }
  }

  const handleExportTeams = () =>
    withLoading(async () => {
      const { teams, profileMap, customerMap, prefMap } = await fetchTeamsData();
      const rows = buildTeamRows(teams, profileMap, customerMap, prefMap);
      exportToCsv(`${leagueName}_laglista.csv`, rows);
    });

  const handleExportBilling = () =>
    withLoading(async () => {
      const { teams, profileMap, customerMap } = await fetchTeamsData();
      const rows = buildBillingRows(teams, profileMap, customerMap);
      exportToCsv(`${leagueName}_fakturering.csv`, rows);
    });

  const handleExportSchedule = () =>
    withLoading(async () => {
      const rows = await fetchScheduleRows();
      exportToCsv(`${leagueName}_spelschema.csv`, rows);
    });

  const handleExportAll = () =>
    withLoading(async () => {
      const { teams, profileMap, customerMap, prefMap } = await fetchTeamsData();
      const teamRows = buildTeamRows(teams, profileMap, customerMap, prefMap);
      const billingRows = buildBillingRows(teams, profileMap, customerMap);
      const scheduleRows = await fetchScheduleRows();

      await exportMultiSheetExcel(`${leagueName}_export.xlsx`, [
        { name: "Laglista", rows: teamRows },
        { name: "Fakturering", rows: billingRows },
        { name: "Spelschema", rows: scheduleRows },
      ]);
    });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          {t("leagues.export.export")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleExportTeams} className="gap-2">
          <FileText className="h-4 w-4" /> {t("leagues.export.teamList")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleExportBilling}
          className="gap-2"
        >
          <FileText className="h-4 w-4" /> {t("leagues.export.billing")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleExportSchedule}
          className="gap-2"
        >
          <Calendar className="h-4 w-4" /> {t("leagues.export.schedule")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportAll} className="gap-2">
          <FileSpreadsheet className="h-4 w-4" /> {t("leagues.export.allExcel")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
